import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { CircularProgress } from "@mui/material";
import { useQueryClient } from "react-query";

import TextField from "../../../Components/Form Fields/text-field";
import CheckBox from "../../../Components/Form Fields/checkbox";
import PrimaryButton from "../../../Components/Buttons/primary-button";

import { useModalContext } from "../../../Contexts/ModalContext";

import { useAddPayee, useValidateRoutingNumber } from "../../../CustomHooks/Mutations";

import { validation } from "../../../Utils/validations";
import { dispatchAPIErrorToast, getUserId } from "../../../Utils/helpers";
import { APIQueryKeys, modalVariants } from "../../../Utils/constants";

import { AccountNumberInCheque, RoutingNumberInCheque } from "../../../Assets/Images";

export default function renderAddPayee(modalContext) {
  const { updateModalComponent, pushModalStack, setModalVariant, setModalTitle } = modalContext;

  updateModalComponent(AddPayee);
  pushModalStack(() => renderAddPayee(modalContext));
  setModalVariant(modalVariants.formDialog);
  setModalTitle("Add New Payee");
}

const AddPayee = () => {
  const debounceRef = useRef(null);
  const [bankName, setBankName] = useState(null);

  const { register, watch, trigger, getFieldState, formState, handleSubmit, reset: resetForm } = useFormContext();
  const queryClient = useQueryClient();

  const { popModalStack } = useModalContext();

  const accountNumber = watch("accountNumber");
  const agreeToTerms = watch("agreeToTerms") ?? false;

  const { isLoading: isValidatingRoutingNumber, mutateAsync: ValidateRoutingNumber } = useValidateRoutingNumber();

  const fullNameProps = register("fullName", {
    required: "Please enter the payee’s full name",
    validate: {
      isAllowedCharacters: (name) =>
        validation.name.isAllowedCharacters(name) || "Full name must not contain unaccepted characters",
      hasAtleast2Characters: (name) =>
        validation.name.hasAtleast2Characters(name) || "Full name must be at least 2 characters long",
      hasAtmost50Characters: (name) =>
        validation.name.hasAtmost50Characters(name) || "Full name can't exceed 50 characters",
    },
  });

  const routingNumberProps = register("routingNumber", {
    required: "Please enter the routing number",
    validate: {
      ...validation.bankRoutingNumber,
      validateRoutingNumber: async (routingNumber) => {
        try {
          const {
            data: {
              data: { bank_name },
            },
          } = await ValidateRoutingNumber(routingNumber);
          setBankName(bank_name);
          return true;
        } catch (err) {
          return err?.response?.data?.error ?? "Error validating routing number.";
        }
      },
    },
  });

  const onRoutingNumberChange = (e) => {
    routingNumberProps.onChange(e);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      trigger("routingNumber");
    }, 300);
  };

  const accountNumberProps = register("accountNumber", {
    required: "Please enter the account number",
    validate: validation.bankAccountNumber,
  });

  const confirmAccountNumberProps = register("confirmAccountNumber", {
    required: "Please confirm the account number",
    validate: (confirmAccountNumber) =>
      accountNumber === confirmAccountNumber || "Account numbers don’t match. Double-check and try again.",
  });

  const agreeToTermsProps = register("agreeToTerms", {
    required: "Please accept the terms and conditions",
  });

  const { error: fullNameFieldError } = getFieldState(fullNameProps.name, formState);
  const { error: routingNumberFieldError } = getFieldState(routingNumberProps.name, formState);
  const { error: accountNumberFieldError } = getFieldState(accountNumberProps.name, formState);
  const { error: confirmAccountNumberFieldError } = getFieldState(confirmAccountNumberProps.name, formState);
  const { error: agreeToTermsFieldError } = getFieldState(agreeToTermsProps.name, formState);

  const { isLoading: isAddingPayee, mutate: AddPayee } = useAddPayee({
    onSuccess: () => {
      queryClient.refetchQueries(APIQueryKeys.payeeList);
      popModalStack(1);
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "addPayee" }),
  });

  useEffect(() => {
    return () => resetForm();
  }, [resetForm]);

  const onSubmit = ({ fullName, routingNumber, accountNumber }) =>
    AddPayee({
      mobile_user_id: getUserId(),
      name: fullName,
      routing_number: routingNumber,
      account_number: accountNumber,
    });

  const isLoading = isAddingPayee || isValidatingRoutingNumber;

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <div className="modal-dialog-content">
        <p className="text-base-regular color--mist mb-4">
          The information will be used to send your rent payments. It will be stored securely.
        </p>
        <TextField
          className="filled mb-4"
          label="Full Name"
          placeholder="Enter payee’s full name"
          {...fullNameProps}
          error={!!fullNameFieldError}
          errorMessage={fullNameFieldError?.message}
        />
        <TextField
          className="filled mb-4"
          label="Routing Number"
          tooltipContent={
            <>
              <img className="w-100 mb-3" src={RoutingNumberInCheque} alt="" />
              <h5 className="text-xs-medium color--mist mb-1">Locate the Routing Number</h5>
              <p className="text-xxs-regular color--mist mb-1">
                The routing number is a 9-digit code that identifies the payee’s bank.
              </p>
              <p className="text-xxs-regular color--mist mb-1">You can find it:</p>
              <ul className="text-xxs-regular color--mist ps-3 mb-0">
                <li className="mb-1">On the bottom left of the payee’s check, or</li>
                <li>By asking the payee to provide their routing number.</li>
              </ul>
            </>
          }
          placeholder="Enter payee’s 9-digit routing number"
          type="number"
          {...routingNumberProps}
          onChange={onRoutingNumberChange}
          error={!!routingNumberFieldError}
          errorMessage={routingNumberFieldError?.message}
          helperText={bankName}
        />
        <TextField
          className="filled mb-4"
          label="Account Number"
          tooltipContent={
            <>
              <img className="w-100 mb-3" src={AccountNumberInCheque} alt="" />
              <h5 className="text-xs-medium color--mist mb-1">Locate the Account Number</h5>
              <p className="text-xxs-regular color--mist mb-1">
                The account number identifies the specific bank account you’re sending money to.
              </p>
              <p className="text-xxs-regular color--mist mb-1">You can find it:</p>
              <ul className="text-xxs-regular color--mist ps-3 mb-0">
                <li className="mb-1">On the payee’s check, usually right next to the routing number, or</li>
                <li>By asking the payee to provide their account number.</li>
              </ul>
            </>
          }
          placeholder="Enter payee’s account number"
          type="number"
          {...accountNumberProps}
          error={!!accountNumberFieldError}
          errorMessage={accountNumberFieldError?.message}
        />
        <TextField
          className="filled mb-4"
          label="Confirm Account Number"
          placeholder="Re-enter payee’s account number"
          type="number"
          {...confirmAccountNumberProps}
          error={!!confirmAccountNumberFieldError}
          errorMessage={confirmAccountNumberFieldError?.message}
        />
        <div className="d-flex align-items-start gap-2">
          <CheckBox {...agreeToTermsProps} checked={agreeToTerms} />
          <p className="text-base-regular color--mist mb-0">
            I confirm that the routing and account numbers entered are correct.
          </p>
        </div>
        {!!agreeToTermsFieldError && (
          <span className="text-xs-regular color--error-red mt-1">{agreeToTermsFieldError?.message}</span>
        )}
      </div>
      <div className="action-container split-equally">
        <PrimaryButton type="submit" disabled={isLoading}>
          Continue {isLoading && <CircularProgress />}
        </PrimaryButton>
        <PrimaryButton className="secondary-colors" type="button" onClick={() => popModalStack(1)}>
          Cancel
        </PrimaryButton>
      </div>
    </form>
  );
};
